import { get } from 'lodash';
import { parse } from 'url';
import cookies from 'js-cookie';
import uuid from 'uuid/v4';
import { AnalyticsTypes, ContentValues, DimensionValues, SettingType } from '../google-analytics';
import { BusinessUnit, EVENT_TYPES, EventDataSchemaVersion } from '../../constants';
import { getPageFromPathname } from '../getPageFromPathname';
import { getProfilePageParameters } from '../../profile/ActiveProfile/state/selectors';
import { getSeoAbResponse } from '../../Shared/Seo';
import { getPageloadTraceId, getBotInfo } from '../../config/pageload/selector';

const pageGuid = uuid().replace(/-/g, '');

const prefixWithTags = (action, category, tags) => {
    const buildingPrefixes = [];

    if (category && category !== '') {
        buildingPrefixes.push(`[${category}]`);
    }

    if (tags && tags.length > 0) {
        buildingPrefixes.push(`[${tags}]`);
    }

    buildingPrefixes.push(action);

    return buildingPrefixes.join(' ');
};

const getZlogsPageType = ({ pageCategory = () => null }, state) => pageCategory(state);
const getZlogsRollupBucket = ({ rollUpBucket = () => null }, state) => rollUpBucket(state);

export default async function getDatalakeEvent({
    event, location, doc, store, initialAnalytics,
}) {
    const bsid = cookies.get('bsid');
    const firstTimeVisitor = cookies.get('firstTimeVisitor');
    const softLoginToken = cookies.get('sut') || null;
    const sessionId = !bsid || bsid === null ? null : bsid.substr(0, bsid.indexOf('_'));
    const trackingId = firstTimeVisitor || null;
    const userAgent = navigator ? navigator.userAgent : null;
    const timestamp = (new Date()).toISOString();
    const state = store.getState();
    const pathname = get(state, 'routing.locationBeforeTransitions.pathname', event.page);
    const pageTraceId = get(getPageloadTraceId(state), 'pageTraceId') || pageGuid;
    const botInfo = get(getBotInfo(state), 'botInfo') || {};
    const zlogPageType = getZlogsPageType(initialAnalytics, state);
    const zlogRollupBucket = getZlogsRollupBucket(initialAnalytics, state);

    let returnValue = null;

    switch (event.type) {
        case AnalyticsTypes.ChangePageView: {
            const providerId = get(getProfilePageParameters(state), 'providerId');
            const seoTestingParameters = getSeoAbResponse(state);
            const { isBot, botDetectionVersion } = botInfo;

            let experiments = [{
                experimentName: null,
                experimentVariant: null,
            }];

            const normalizedUrl = parse(location.href).pathname;

            if (seoTestingParameters && seoTestingParameters.assignments) {
                // GraphQL requests seo.assignments on SearchPage to be returned as an array
                // of type [{ key: <experiment name>, value: <experiment group> }]
                // However Profile/other pages were never migrated to this; instead they are stored in redux as
                // type { <experimentA>: "<experiment group>", <experimentB>: "<experiment group>", ... }
                // We need to eventually convert remaining assignments queries to match search,
                // and clarify this in redux (e.g. set default types) in Shared/Seo/reducer.js
                // For now this hack checks the type of assignmnets
                if (Array.isArray(seoTestingParameters.assignments)) {
                    if (seoTestingParameters.assignments.length > 0) {
                        experiments = seoTestingParameters.assignments.map(({ key, value }) => ({
                            experimentName: key,
                            experimentVariant: value,
                        }));
                    }
                } else {
                    // this should go away once we switch other pages to fetchSeo via GraphQL as well
                    const experimentNames = Object.keys(seoTestingParameters.assignments);
                    if (experimentNames.length > 0) {
                        experiments = experimentNames.reduce((arr, experimentName) => {
                            arr.push({
                                experimentName,
                                experimentVariant: seoTestingParameters.assignments[experimentName],
                            });
                            return arr;
                        }, []);
                    }
                }
            }

            returnValue = {
                BusinessUnit: BusinessUnit.PATIENT_WEB_APP,
                Version: EventDataSchemaVersion[BusinessUnit.PATIENT_WEB_APP][EVENT_TYPES.Pageview],
                Name: EVENT_TYPES.Pageview,
                EventData: [{
                    Name: doc.title,
                    CurrentUrl: location.href,
                    PreviousUrl: doc.referrer,
                    BrowserTimestamp: timestamp,
                    SessionId: sessionId,
                    TrackingId: trackingId,
                    SoftLoginToken: softLoginToken,
                    UserAgent: userAgent,
                    PageId: pageTraceId,
                    IsWhiteLabel: event.isWhiteLabel,
                    WhiteLabelName: event.whiteLabelName,
                    WhiteLabelDirectoryId: event.directoryId,
                    WhiteLabelStrategicId: event.strategicIds,
                    ProviderId: providerId,
                    NormalizedUrl: normalizedUrl,
                    Experiments: experiments,
                    IsBot: isBot,
                    BotDetectionVersion: botDetectionVersion,
                    InsuranceCarrierHealth: cookies.get('hCarrierId') || null,
                    InsurancePlanHealth: cookies.get('hPlanId') || null,
                    InsuranceCarrierDental: cookies.get('dCarrierId') || null,
                    InsurancePlanDental: cookies.get('dPlanId') || null,
                    InsuranceCarrierVision: cookies.get('vCarrierId') || null,
                    InsurancePlanVision: cookies.get('vPlanId') || null,
                    ZlogPageType: zlogPageType,
                    ZlogRollupBucket: zlogRollupBucket,
                }],
            };
            break;
        }

        case AnalyticsTypes.SetDimension: {
            if (event.settingType === SettingType.ContentGroup) {
                returnValue = {
                    BusinessUnit: BusinessUnit.PATIENT_WEB_APP,
                    Version: EventDataSchemaVersion[BusinessUnit.PATIENT_WEB_APP][EVENT_TYPES.SetContentGroup],
                    Name: EVENT_TYPES.SetContentGroup,
                    EventData: [{
                        Name: ContentValues[event.index],
                        Value: event.value,
                        BrowserTimestamp: timestamp,
                        SessionId: sessionId,
                        TrackingId: trackingId,
                        SoftLoginToken: softLoginToken,
                        UserAgent: userAgent,
                        PageId: pageTraceId,
                    }],
                };
            } else if (event.settingType === SettingType.CustomDimension) {
                returnValue = {
                    BusinessUnit: BusinessUnit.PATIENT_WEB_APP,
                    Version: EventDataSchemaVersion[BusinessUnit.PATIENT_WEB_APP][EVENT_TYPES.SetCustomDimension],
                    Name: EVENT_TYPES.SetCustomDimension,
                    EventData: [{
                        Name: DimensionValues[event.index],
                        Value: event.value,
                        BrowserTimestamp: timestamp,
                        SessionId: sessionId,
                        TrackingId: trackingId,
                        SoftLoginToken: softLoginToken,
                        UserAgent: userAgent,
                        PageId: pageTraceId,
                    }],
                };
            }
            break;
        }

        case AnalyticsTypes.TrackEvent: {
            const listedCategory = event.category || '';
            const listedAction = prefixWithTags(event.action, event.category, event.tags) || '';

            returnValue = {
                BusinessUnit: BusinessUnit.PATIENT_WEB_APP,
                Version: EventDataSchemaVersion[BusinessUnit.PATIENT_WEB_APP][EVENT_TYPES.Action],
                Name: EVENT_TYPES.Action,
                EventData: [{
                    ...event.data,
                    Name: listedAction,
                    ActionName: listedAction,
                    Category: listedCategory,
                    BrowserTimestamp: timestamp,
                    SessionId: sessionId,
                    Label: event.label,
                    Value: event.value,
                    TrackingId: trackingId,
                    SoftLoginToken: softLoginToken,
                    UserAgent: userAgent,
                    PageId: pageTraceId,
                    ZlogPageType: zlogPageType,
                    ZlogRollupBucket: zlogRollupBucket,
                }],
            };
            break;
        }

        case AnalyticsTypes.TrackPreviousMonolithEvent: {
            const listedCategory = event.category || '';
            const listedAction = prefixWithTags(event.action, event.category, event.tags) || '';
            const listedPageId = event.pageId || pageTraceId;

            returnValue = {
                BusinessUnit: BusinessUnit.MONOLITH_GA_ACTION,
                Version: EventDataSchemaVersion[BusinessUnit.MONOLITH_APP][EVENT_TYPES.Action],
                Name: EVENT_TYPES.Action,
                EventData: [{
                    ...event.data,
                    ActionName: listedAction,
                    BrowserTimestamp: timestamp,
                    Category: listedCategory,
                    Label: event.label,
                    Name: listedAction,
                    PageId: listedPageId,
                    SessionId: sessionId,
                    TrackingId: trackingId,
                    SoftLoginToken: softLoginToken,
                    UserAgent: userAgent,
                    ZlogPageType: zlogPageType,
                    ZlogRollupBucket: zlogRollupBucket,
                }],
            };
            break;
        }
    }

    if (returnValue && pathname) {
        try {
            const { pageName, category } = await getPageFromPathname(pathname);

            returnValue.EventData.forEach(ed => {
                ed.Page = pageName;
                ed.Category = ed.Category || category;
            });
        } catch (err) {
            /* */
        }
    }

    return returnValue;
}
